import { AuthUser, AuthTokens, AuthUserSchema } from './types';

const ACCESS_TOKEN_KEY = 'self_managed_access_token';
const REFRESH_TOKEN_KEY = 'self_managed_refresh_token';
const USER_KEY = 'self_managed_user';
const TOKEN_EXPIRES_AT_KEY = 'self_managed_token_expires_at';

/**
 * Storage utilities for auth-v2 tokens and user data.
 * Uses localStorage for persistence across page refreshes.
 */
export const authStorage = {
    /**
     * Store tokens in localStorage and dispatch change event
     */
    storeTokens(tokens: AuthTokens): void {
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        // Store expiration time (expiresIn is in seconds, convert to milliseconds)
        const expiresAt = Date.now() + tokens.expiresIn * 1000;
        localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAt.toString());
        // Dispatch event to notify auth providers that tokens have changed
        window.dispatchEvent(new CustomEvent('auth-tokens-changed'));
    },

    /**
     * Store user in localStorage
     */
    storeUser(user: AuthUser): void {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    },

    /**
     * Get the current access token
     */
    getAccessToken(): string | null {
        return localStorage.getItem(ACCESS_TOKEN_KEY);
    },

    /**
     * Get the current refresh token
     */
    getRefreshToken(): string | null {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Get the current user (validated via Zod schema)
     */
    getUser(): AuthUser | null {
        const userStr = localStorage.getItem(USER_KEY);
        if (!userStr) return null;
        try {
            return AuthUserSchema.parse(JSON.parse(userStr));
        } catch {
            return null;
        }
    },

    /**
     * Check if the access token has expired
     */
    isTokenExpired(): boolean {
        const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
        if (!expiresAt) return true;
        return Date.now() >= parseInt(expiresAt, 10);
    },

    /**
     * Check if user is authenticated (token exists and is not expired)
     */
    isAuthenticated(): boolean {
        return !!this.getAccessToken() && !this.isTokenExpired();
    },

    /**
     * Clear all stored tokens and user data
     */
    clearAll(): void {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
        localStorage.removeItem(USER_KEY);
    },
};
